import React from 'react';

export default function AdaptersExplanation() {
    const [selectedSection, setSelectedSection] = React.useState('definition');

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                    <h2 className="text-2xl font-bold mb-4">Infrastructure Adapters</h2>
                    
                    <div className="mb-4 flex space-x-2">
                        {['definition', 'structure', 'examples', 'implementation'].map(section => (
                            <button
                                key={section}
                                onClick={() => setSelectedSection(section)}
                                className={`px-4 py-2 rounded ${
                                    selectedSection === section
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-100'
                                }`}
                            >
                                {section.charAt(0).toUpperCase() + section.slice(1)}
                            </button>
                        ))}
                    </div>

                    {selectedSection === 'definition' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Qu'est-ce qu'un Adapter ?</h3>
                            <ul className="space-y-2 list-disc list-inside text-gray-700">
                                <li>Implémente les ports de l'application</li>
                                <li>Convertit entre les formats internes et externes</li>
                                <li>Gère les détails techniques d'intégration</li>
                                <li>Isole la complexité technique</li>
                                <li>Assure la résilience et le retry</li>
                            </ul>
                        </div>
                    )}

                    {selectedSection === 'structure' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Structure Type</h3>
                            <pre className="bg-gray-50 p-4 rounded text-sm">
{`@Component
@Slf4j
public class SwiftNetworkAdapter implements PaymentGatewayPort {
    private final SwiftClient swiftClient;
    private final SwiftMessageMapper mapper;
    private final RetryTemplate retryTemplate;

    @Override
    public GatewayResponse sendPayment(Payment payment) {
        try {
            log.debug("Preparing SWIFT message for payment: {}", 
                     payment.getId());

            // 1. Conversion vers format SWIFT
            SwiftMessage swiftMessage = mapper.toSwiftFormat(payment);

            // 2. Envoi avec retry
            return retryTemplate.execute(context -> {
                try {
                    // Envoi au réseau SWIFT
                    SwiftResponse response = swiftClient.sendMessage(
                        swiftMessage,
                        buildOptions(payment)
                    );

                    // Mapping de la réponse
                    return mapToGatewayResponse(response);

                } catch (SwiftException e) {
                    handleSwiftError(e, context);
                    throw e;
                }
            });

        } catch (Exception e) {
            log.error("Failed to send payment via SWIFT", e);
            return GatewayResponse.error(e);
        }
    }

    private SwiftOptions buildOptions(Payment payment) {
        return SwiftOptions.builder()
            .priority(mapPriority(payment.getPriority()))
            .category(determineMsgCategory(payment))
            .build();
    }

    private void handleSwiftError(SwiftException e, 
                                RetryContext context) {
        log.warn("SWIFT error on attempt {}: {}", 
                context.getRetryCount(), 
                e.getMessage());
    }
}`}
                            </pre>
                        </div>
                    )}

                    {selectedSection === 'examples' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Exemples d'Adapters</h3>
                            
                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">Persistance JPA</h4>
                                <pre className="text-xs mt-2">
{`@Component
public class JpaPaymentRepositoryAdapter 
    implements PaymentRepositoryPort {
    
    private final JpaPaymentRepository jpaRepository;
    private final PaymentEntityMapper mapper;

    @Override
    @Transactional
    public Payment save(Payment payment) {
        try {
            // Conversion en entité JPA
            PaymentEntity entity = mapper.toEntity(payment);
            
            // Sauvegarde
            entity = jpaRepository.save(entity);
            
            // Reconversion en objet domaine
            return mapper.toDomain(entity);
            
        } catch (DataIntegrityViolationException e) {
            throw new PersistenceException(
                "Failed to save payment", e);
        }
    }

    @Override
    @Transactional(readOnly = true)
    public Optional<Payment> findById(UUID id) {
        return jpaRepository.findById(id)
            .map(mapper::toDomain);
    }
}`}
                                </pre>
                            </div>

                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">Messaging avec Kafka</h4>
                                <pre className="text-xs mt-2">
{`@Component
@Slf4j
public class KafkaEventPublisherAdapter 
    implements EventPublisherPort {
    
    private final KafkaTemplate<String, EventMessage> kafka;
    private final EventMapper mapper;

    @Override
    public void publishEvent(DomainEvent event) {
        try {
            // Conversion en message Kafka
            EventMessage message = mapper.toMessage(event);
            String topic = determineTopic(event);
            
            // Publication avec callback
            kafka.send(topic, message.getKey(), message)
                .addCallback(
                    result -> handleSuccess(result, event),
                    error -> handleError(error, event)
                );
                
        } catch (Exception e) {
            log.error("Failed to publish event", e);
            throw new EventPublishingException(
                "Event publishing failed", e);
        }
    }

    private void handleSuccess(SendResult<String, EventMessage> result,
                             DomainEvent event) {
        log.debug("Event published successfully: {}", 
                 event.getEventId());
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}

                    {selectedSection === 'implementation' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Patterns d'Implémentation</h3>
                            
                            <div className="space-y-2">
                                <h4 className="font-medium">1. Pattern Retry</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`@Component
public class RetryableAdapter {
    private final RetryTemplate retry;

    public <T> T executeWithRetry(Supplier<T> operation) {
        return retry.execute(context -> {
            try {
                return operation.get();
            } catch (Exception e) {
                log.warn("Attempt {} failed: {}", 
                    context.getRetryCount(), 
                    e.getMessage());
                throw e;
            }
        });
    }
}`}
                                </pre>
                            </div>

                            <div className="space-y-2">
                                <h4 className="font-medium">2. Circuit Breaker</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`@Component
public class ResilientAdapter {
    private final CircuitBreaker circuitBreaker;

    public <T> T executeWithCircuitBreaker(
        Supplier<T> operation
    ) {
        return circuitBreaker.run(
            () -> operation.get(),
            throwable -> handleFallback(throwable)
        );
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-lg p-4 shadow">
                    <h3 className="text-xl font-bold mb-4">Bonnes Pratiques</h3>
                    
                    <div className="space-y-4">
                        <div className="border-l-4 border-blue-500 pl-4">
                            <h4 className="font-semibold">1. Résilience</h4>
                            <p className="text-sm text-gray-600">
                                Implémenter des mécanismes de retry et circuit breaker
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                RetryTemplate, CircuitBreaker
                            </code>
                        </div>

                        <div className="border-l-4 border-green-500 pl-4">
                            <h4 className="font-semibold">2. Mapping Propre</h4>
                            <p className="text-sm text-gray-600">
                                Convertir proprement entre formats domaine et externe
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                Mappers dédiés, conversion explicite
                            </code>
                        </div>

                        <div className="border-l-4 border-purple-500 pl-4">
                            <h4 className="font-semibold">3. Gestion d'Erreurs</h4>
                            <p className="text-sm text-gray-600">
                                Traduire les erreurs techniques en erreurs métier
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                Exception mapping, error handling
                            </code>
                        </div>

                        <div className="border-l-4 border-yellow-500 pl-4">
                            <h4 className="font-semibold">4. Configuration</h4>
                            <p className="text-sm text-gray-600">
                                Externaliser la configuration technique
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                Properties, external configuration
                            </code>
                        </div>

                        <div className="border-l-4 border-red-500 pl-4">
                            <h4 className="font-semibold">5. Monitoring</h4>
                            <p className="text-sm text-gray-600">
                                Implémenter des métriques et traces
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                Metrics, tracing, logging
                            </code>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}